import React, { useState, useEffect, useRef, useCallback } from 'react';
import useUploadContext from 'hooks/useUploadContext';
import { useGetDrafts, useGetDraft, useDeleteDraft } from 'hooks/uploadMutations';
import DeleteConfirm from "components/UploadPage/LeftSidebar/Drafts/DeleteConfirm";
import DraftInfo from "components/UploadPage/LeftSidebar/Drafts/DraftInfo";
import TinyGesture from 'tinygesture';
import LayersDeleteIcon from "assets/images/layers-delete-icon.png";
import LayersInfoIcon from "assets/images/layers-info-icon.png";
import BackRight from "assets/images/backRight.png";
import Magnifyer from "assets/images/magnifyer.png";
import Box from "@mui/material/Box";

import "./index.scss";


const Drafts = () => {
    const {
        drafts,
        setDrafts,
        selectedDraft,
        setSelectedDraft,
        leftTab,
        setLeftTab,
        setTotalTime,
        setLayers,
        loading,
        setLoading,
        audioContextRef,
        setMutes,
        setTempo,
        handleResetProject
    } = useUploadContext();

    const { mutateAsync: getDrafts } = useGetDrafts();
    const { mutateAsync: getDraft } = useGetDraft();
    const { mutate: deleteDraftSync } = useDeleteDraft();

    const [inputString, setInputString] = useState('');
    const refDrafts = useRef([]);
    const [animStates, setAnimStates] = useState([]);


    const handleDelete = (e) => {
        e.preventDefault();
        if (selectedDraft !== null) {
            setLeftTab('draftDeleteConfirm');
        }
    };

    const handleInfo = (e) => {
        e.preventDefault();
        if (selectedDraft !== null) {
            setLeftTab('draftInfo')
        }
    };

    const handleCancel = () => {
        setLeftTab('drafts');
    };

    const deleteDraft = async () => {
        try {
            setLoading(true);
            const data = await deleteDraftSync(selectedDraft.id);
            if (data?.status === "OK") {
                handleResetProject();
                setLoading(false);
            } else {
                throw new Error('Error deleting draft');
            }
        } catch (err) {
            console.log('خطایی در حذف درفت رخ داده است.',{ variant: 'error'});
        }
    };
      
    const handleFileFromURL = async (layer) => {
        let newLayer = {
            file: null,
            title: layer.name, 
            locked: false,
            created: new Date(),
            length: layer.duration,
            tempo: layer.soundTempo,
            url: layer.url, 
            id: layer.id,
            fileId: layer.id,
        };

        if (layer?.url) {
            try {
                const response = await fetch(layer?.url);
                if (!response.ok) {
                    throw new Error('Failed to fetch audio file');
                }

                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();
                const audioBuffer = await audioContextRef?.current.decodeAudioData(arrayBuffer);

                setTotalTime((pre)=> {
                    if (audioBuffer.duration > pre) {
                        return(audioBuffer.duration);
                    } else {
                        return pre;
                    }
                });
                newLayer.file = {...layer.fileMetadata, blob};
                newLayer.length = audioBuffer.duration;
        
            } catch (error) {
                console.error('Error during file upload or draft creation:', error);
            }
        }
        return newLayer;
    };
    
    const handleOpenDraft = async () => {
        try {
            setLoading(true);
            const data = await getDraft(selectedDraft.id);
            if (data?.status === "OK") {
                const { draft } = data;
                const newDraft = {
                    id: draft.draftId,
                    draftId: draft.draftId,
                    length: draft.duration,
                    tempo: draft.defaultTempo,
                    created: draft.createdAt,
                    modified: draft.updatedAt,
                    name: draft.title,
                    title: draft.title,
                    description: draft.description,
                    ...draft,
                }
                setSelectedDraft(newDraft);
                setTempo(draft.defaultTempo)
                const newMutes = draft.layers.map(({active}) => (active));
                setMutes(newMutes)

                const draftLayers = draft.layers.map(async (layer) => {
                    return await handleFileFromURL(layer);
                });
                const newLayers = await Promise.all(draftLayers);
                
                setLayers(newLayers);
                setLeftTab('layers');
                setLoading(false);
            } else {
                throw new Error('Error getting draft');
            }
        } catch (err) {
            console.log('خطایی در درفت درفت رخ داده است.',{ variant: 'error'});
        }
    };

    const handleBack = () => {
        setLeftTab(null);
    };

    const handleSearchInput = (e) => {
        const newValue = e.target.value;
        setInputString(newValue);
    };

    const initSlider = useCallback((target) => {
        let swiped = 0;
        let oldSwiped = 0;
        const revealWidth = 60;
        const gesture = new TinyGesture(target);
        const draftIndex = target.id;

        const resetPan = () => {
            const draftBoxes = refDrafts?.current.map((draftRef) => (draftRef.draftBox));
            draftBoxes.forEach((draftBox) => {
                if (draftBox) {
                    window.cancelAnimationFrame(gesture.animationFrame);
                    gesture.animationFrame = null;
                    window.requestAnimationFrame(() => {
                        draftBox.style.transform = null;
                    });
                }
            });
        };

        const handlePanStart = () => {
            resetPan();
            const findDraft = drafts.find(({id}) => id === draftIndex)
            setSelectedDraft(findDraft)
        };

        const handlePanMove = () => {
            if (gesture.animationFrame) return;
            gesture.animationFrame = window.requestAnimationFrame(() => {
                const newX = gesture.touchMoveX;
                target.style.transform = "translateX(" + newX + "px)";
                swiped = newX < 0 ? -revealWidth : newX > 0  ? revealWidth : 0;
            });
        };

        const handlePanEnd = () => {
            window.requestAnimationFrame(() => {
                target.style.transition = "transform .2s ease-in";
                if (swiped) {                       
                    if(Math.abs(oldSwiped) > 0) {
                        target.style.transform = null;
                        oldSwiped = 0;
                        swiped = 0;
                    } else {
                        target.style.transform = "translateX(" + swiped + "px)";
                        oldSwiped = swiped;
                    }
                } else {
                    setLeftTab('draftInfo');
                }
            });
        };

        const handleMouseleave = () => {
            window.cancelAnimationFrame(gesture.animationFrame);
            gesture.animationFrame = null;
        };

        const handleClickOutside = (event) => {
            // event.preventDefault();
            if (!target.contains(event.target)) {
                resetPan();
            }
        };

        gesture.on("panmove", handlePanMove);
        gesture.on("panend", handlePanEnd);
        gesture.on("panstart", handlePanStart);
        target.addEventListener("mouseleave", handleMouseleave);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            target.removeEventListener("mouseleave", handleMouseleave);
        };

    }, [drafts, setSelectedDraft, setLeftTab]);

    useEffect(() => {
        const handleGetDrafts = async() => {
            try {
                setLoading(true);
                const data = await getDrafts();
                if (data?.status === "OK") {
                    const myDrafts = data.drafts.map((myDraft) => ({
                        id: myDraft.draftId,
                        length: myDraft.duration,
                        tempo: myDraft.defaultTempo,
                        created: myDraft.createdAt,
                        modified: myDraft.updatedAt,
                        name: myDraft.title,
                        description: myDraft.description,
                    }))
                    setDrafts(myDrafts);
                    setLoading(false);
                } else {
                    throw new Error('Error getting drafta');
                }
            } catch(err){
                console.log('خطایی در درفت درفت ها رخ داده است.',{ variant: 'error'});
            }
        };
        handleGetDrafts();
    }, [getDrafts, setDrafts, setLoading])

    useEffect(() => {
        document.querySelectorAll(".draft-box").forEach(initSlider);
    }, [initSlider, drafts, leftTab]);

    useEffect(() => {
        const newAnimStates = drafts.map((_, i) => {
            const container = refDrafts.current[i]?.container;
            const content = refDrafts.current[i]?.content;
            if (container && content) {
                return content.scrollWidth > container.offsetWidth;
            }
            return false;
        });
        setAnimStates(newAnimStates);
    }, [drafts, selectedDraft]);

    return (
        <Box className="drafts-box-component">
            <Box className="drafts-bar-box">
                <Box
                    className="parent-backSpace"
                    onClick={handleBack}
                >
                    <img src={BackRight} alt="backSpace" />
                </Box>
                <span>Drafts</span>
            </Box>
            <Box className="drafts-search-box">
                <img src={Magnifyer} alt="Magnifyer" />
                <input type="text" placeholder='Search' onChange={handleSearchInput} value={inputString} />
            </Box>
            <Box className="drafts-list-box">
                {
                    leftTab === 'draftInfo' ? (
                        <DraftInfo
                            data={selectedDraft}
                            onOpen={handleOpenDraft}
                            onBack={handleCancel}
                            loading={loading}
                        />
                    ) : leftTab === 'draftDeleteConfirm' ? (
                        <DeleteConfirm
                            data={selectedDraft}
                            deleteDraft={deleteDraft}
                            cancel={handleCancel}
                        />
                    ) : leftTab === 'drafts' ? ( drafts.length ?
                        drafts.map((draft, i) => (
                            <Box
                                key={draft.id}
                                id={draft.id}
                                className={`draft-box ${selectedDraft?.id === draft.id ? 'active-draft' : ''}`}
                                ref={(el) => (refDrafts.current[i] = { ...refDrafts.current[i], draftBox: el })}
                            >
                                <Box className="info-icon" onClick={handleInfo}>
                                    <img
                                        src={LayersInfoIcon}
                                        alt="draft info icon"
                                    />
                                </Box>
                                <Box
                                    className="title"
                                    ref={(el) => (refDrafts.current[i] = { ...refDrafts.current[i], container: el })}
                                >
                                    <div
                                        className={`${animStates[i] ? ' animate' : ''} ${selectedDraft?.id === draft.id ? 'active' : ''}`}
                                        ref={(el) => (refDrafts.current[i] = { ...refDrafts.current[i], content: el })}
                                    >
                                        {draft?.name ? draft.name : `draft ${i}`}
                                    </div>
                                </Box>
                                <Box className="delete-icon" onClick={handleDelete}>
                                    <img
                                        src={LayersDeleteIcon}
                                        alt="draft delete icon"
                                    />
                                </Box>
                            </Box>
                        ))
                        : <Box className="no-draft">!درفتی یافت نشد</Box>
                    )
                    : null
                }
            </Box>
        </Box>
    );
};
export default Drafts;